
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getStatusColor } from "../utils/statusColors";
import { toast } from "sonner";
import { useCartStore } from "@/stores/newCartStore";
import { ShoppingCart, PackagePlus, ArrowLeft } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface StockTableRowProps {
  item: any;
  quantity: number;
  onQuantityChange: (value: string) => void;
  onAddToCart: (item: any, quantity: number) => void;
  onRestock: () => void;
  onReturn?: () => void;
  buttonVisibility: {
    cart: boolean;
    restock: boolean;
    return: boolean;
    quantity: boolean;
    actions: boolean;
  };
  formatPrice: (price: string | number) => string;
}

export const StockTableRow = ({
  item,
  quantity,
  onQuantityChange,
  onAddToCart,
  onRestock,
  onReturn,
  buttonVisibility,
  formatPrice
}: StockTableRowProps) => {
  const {
    addItem
  } = useCartStore();

  const handleAddToCart = async () => {
    try {
      if (quantity <= 0) {
        toast.error("La quantité doit être supérieure à 0");
        return;
      }
      if (quantity > (item.quantity || 0)) {
        toast.error("Quantité insuffisante en stock");
        return;
      }
      await addItem(item.id, quantity);
      onAddToCart(item, quantity);
    } catch (error) {
      console.error("Erreur lors de l'ajout au panier:", error);
      toast.error("Erreur lors de l'ajout au panier");
    }
  };

  const getStatus = (currentQuantity: number, minQuantity: number) => {
    if (currentQuantity <= 0) return "Rupture";
    if (currentQuantity <= minQuantity) return "Stock bas";
    return "En stock";
  };

  const price = item?.data?.price || 0;
  const name = item?.data?.name || 'N/A';
  const unit = item?.data?.unit || 'Pièce';
  const minQuantity = item?.data?.minQuantity || 0;
  const currentStatus = getStatus(item.quantity || 0, minQuantity);

  return <TableRow className="hover:bg-gray-50/80 flex flex-col md:table-row border-b p-4 bg-white my-[15px]">
      <TableCell className="font-medium whitespace-nowrap">
        <span className="font-bold text-gray-900">{name}</span>
      </TableCell>
      <TableCell className="whitespace-nowrap text-gray-700">
        {item.quantity || 0} {unit}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right font-medium text-gray-900">
        <div className="flex flex-col items-end">
          <span className="md:hidden text-xs text-gray-500 mb-1">Prix de vente unitaire:</span>
          {formatPrice(price)}
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(currentStatus)}`}>
          {currentStatus}
        </span>
      </TableCell>
      {buttonVisibility.actions && <TableCell className="text-right">
          <div className="flex flex-row items-center justify-end gap-2">
            {buttonVisibility.quantity && <div className="flex flex-row items-center gap-2">
                <div className="flex flex-col">
                  <Input 
                    type="number" 
                    min="1" 
                    max={item.quantity || 0} 
                    value={quantity} 
                    onChange={e => onQuantityChange(e.target.value)}
                    placeholder="Qté panier" 
                    title="Quantité à ajouter au panier" 
                    aria-label="Quantité à ajouter au panier"
                    className="w-20 border-gray-200 focus:ring-primary-500 focus:border-primary-500 rounded-md bg-white hover:bg-gray-50 text-gray-900 py-2 px-3" 
                  />
                  <span className="text-xs text-gray-500 mt-1">Qté panier</span>
                </div>
              </div>}
            <div className="flex flex-row items-center gap-2">
              {buttonVisibility.cart && <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-primary-600 hover:text-primary-700 hover:bg-primary-50 w-10 h-10 p-0" onClick={handleAddToCart} disabled={!item.quantity || item.quantity === 0 || quantity > item.quantity}>
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Ajouter au panier</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>}
              {buttonVisibility.restock && <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-primary-600 hover:text-primary-700 hover:bg-primary-50 w-10 h-10 p-0" onClick={onRestock}>
                        <PackagePlus className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Réapprovisionner</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>}
              {buttonVisibility.return && onReturn && <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 w-10 h-10 p-0" onClick={onReturn}>
                        <ArrowLeft className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Retour</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>}
            </div>
          </div>
        </TableCell>}
    </TableRow>;
};
